import { useStyles } from "./Paywall.style";
import {
  FC,
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { Typography } from "../../components/core/Typography/Typography";
import CloudConsole1x from "../../assets/subscription/console_1x.webp";
import CloudConsole2x from "../../assets/subscription/console_2x.webp";
import CloudConsole3x from "../../assets/subscription/console_3x.webp";
import ConsoleBundle1x from "../../assets/subscription/console_bundle_1x.webp";
import ConsoleBundle2x from "../../assets/subscription/console_bundle_2x.webp";
import ConsoleBundle3x from "../../assets/subscription/console_bundle_3x.webp";
import FlashIcon from "../../assets/subscription/flash.svg";

import { StaticImage } from "../../components/core/StaticImage/StaticImage";
import { TypographyColor, TypographyVariant } from "../../constants/typography";
import Button from "../../components/core/Button/Button";
import clsx from "classnames";
import { useQuery } from "@apollo/client";
import {
  getSubscriptionPlansGQL,
  meAAAGQL,
  Query,
  SubscriptionPlan,
  User,
} from "@onmo/graphql/graphql";
import { setSkippedActivationCode } from "../../models/storage";
import { AnalyticsGA4 } from "../../models/analyticsGA4";
import ActivateQrPopup from "../../components/common/ActivateQrPopup/ActivateQrPopup";
import ConfirmSubscriptionPopup from "../../components/common/ConfirmSubscriptionPopup/ConfirmSubscriptionPopup";
import BillingInfoPopup from "../../components/common/BillingInfoPopup/BillingInfoPopup";
import { isTV } from "../../utils/platform";
import { PageRoutes } from "../../constants/routes";
import { Icon } from "../../components/core/Icon/Icon";
import {
  useActiveElement,
  useGamepadContext,
} from "../../contexts/GamepadProvider";
import { animatedScrollIntoView } from "../../utils/smoothScroll";
import { useAppSelector } from "../../components/hooks";
import { selectIsDesktopView } from "../../store/slices/appState";
import SubscriptionTv from "../../assets/subscription/paywall_bg.webp";
import { useSubscriptionFlow } from "../../hooks/useSubscriptionFlow";
import { LoadingScreen } from "../../components/common/LoadingScreen/LoadingScreen";
import { useBackNavigation } from "../../hooks/useBackNavigation";
import AccountMismatchPopup from "../../components/common/AccountMismatchPopup/AccountMismatchPopup";
import { useTranslation } from "react-i18next";
import { getCurrencySymbol } from "../../models/user";

const FOUNDER_TAG = "op_founders";

const Paywall: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const goBack = useBackNavigation();
  const [activateQRLink, setActivateQRLink] = useState("");
  const [isMismatchPopupOpen, setIsMismatchPopupOpen] = useState(false);
  const { fromTo } = (location.state ?? {}) as {
    fromTo: "onboarding" | "console" | "game";
  };
  const { data: dataSubscriptionPlans, loading: loadingPlans } =
    useQuery<Query>(getSubscriptionPlansGQL, {
      variables: { input: { productId: "aaa" } },
    });

  const { data: meData, loading: loadingMe } = useQuery<{ me: User }>(meAAAGQL);

  const isLoading = loadingPlans || loadingMe;

  const isFounder = !!meData?.me?.metadata?.tags?.includes(FOUNDER_TAG);

  const tvUserId = useMemo(
    () => new URLSearchParams(location.search).get("tvUserId"),
    [location.search],
  );

  const isDesktop = useAppSelector(selectIsDesktopView);
  const { setCallback, cleanup } = useGamepadContext();
  const activeElement = useActiveElement();
  const bottomBarRef = useRef<HTMLDivElement>(null);

  const { withoutController, firstPlan } = useMemo(() => {
    const plans = dataSubscriptionPlans?.subscriptionPlans;
    const withoutController = plans?.items.find(
      (plan: SubscriptionPlan) => plan?.metadata?.type === "without_controller",
    );
    const firstPlan = plans?.items.find(
      (plan: SubscriptionPlan) =>
        plan?.metadata?.type === "with_controller" &&
        plan?.metadata?.shouldDisplay,
    );

    return {
      withoutController,
      firstPlan,
    };
  }, [dataSubscriptionPlans]);


  useEffect(() => {
    if (!tvUserId || isTV || !meData?.me?.id) return;
    if (tvUserId !== meData.me.id) {
      setIsMismatchPopupOpen(true);
    }
  }, [tvUserId, meData?.me?.id]);

  const {
    start,
    close: closeSubscriptionFlow,
    confirm: confirmSubscription,
    submitBilling,
    plan: selectedPlan,
    isConfirmOpen,
    isBillingOpen,
  } = useSubscriptionFlow();

  const handleClose = useCallback(() => {
    AnalyticsGA4.paywallSelect("skip");
    setSkippedActivationCode();
    goBack();
  }, [goBack]);

  const subscribeProController = () => {
    AnalyticsGA4.paywallSelect("bundle");
    if (isTV) {
      setActivateQRLink(firstPlan?.metadata?.purchaseUrl);
    } else {
      window.open(firstPlan?.metadata.purchaseUrl, "_blank");
    }
  };

  const subscribeWithoutController = () => {
    if (!withoutController) return;
    AnalyticsGA4.paywallSelect("subscribe");
    if (isTV) {
      const urlPaywall = new URL(
        `${import.meta.env.VITE_APP_URL}${PageRoutes.Paywall}`,
      );
      if (meData?.me?.id) urlPaywall.searchParams.set("tvUserId", meData.me.id);

      setActivateQRLink(urlPaywall.toString());
      return;
    }
    start(withoutController);
  };

  useEffect(() => {
    setCallback({ onBackAction: handleClose });
    return () => {
      cleanup();
    };
  }, [setCallback, cleanup, handleClose]);

  useEffect(() => {
    AnalyticsGA4.paywallView(fromTo, "no_subscription");
  }, [fromTo]);

  useEffect(() => {
    const card = activeElement?.closest<HTMLElement>("[data-paywall-card]");
    if (!card) return;
    animatedScrollIntoView(card, {
      axis: "y",
      block: "end",
      offsetBottom: bottomBarRef.current?.offsetHeight ?? 0,
    });
  }, [activeElement]);

  const renderBundleCard = () => (
    <div className={classes.bundleConsoleSection} data-paywall-card>
      <div className={classes.headerTag}>
        <Typography
          variant={TypographyVariant.BodyB4Bold}
          className={classes.tagText}
        >
          {t("Paywall_Best Way to Start")}
        </Typography>
      </div>
      <div className={classes.bundleConsoleContent}>
        <div className={classes.bundleConsoleText}>
          <Typography
            variant={TypographyVariant.HeadingH2Bold}
            className={classes.bundleTitle}
          >
            {t("Paywall_Smart Console Bundle")}
          </Typography>
          <Typography
            variant={TypographyVariant.BodyB3Regular}
            className={classes.bundleSubtitle}
          >
            {t("Paywall_Pro Controller + Cloud Console Access")}
          </Typography>
        </div>
        <StaticImage
          src={ConsoleBundle1x}
          src2x={ConsoleBundle2x}
          src3x={ConsoleBundle3x}
          className={classes.bundleConsoleImage}
        />
        <div className={classes.bundleConsoleBottom}>
          {/*<div className={classes.launchOffer}>
            <StaticImage src={FlashIcon} className={classes.flashIcon} />
            <div className={classes.launchOfferLabels}>
              <Typography
                variant={TypographyVariant.BodyB5Bold}
                className={classes.launchLabel}
              >
                {t("Paywall_LAUNCH")}
              </Typography>
              <Typography
                variant={TypographyVariant.BodyB2Bold}
                className={classes.offerLabel}
              >
                {t("Paywall_OFFER")}
              </Typography>
            </div>
            <div className={classes.dot}></div>
            <div className={classes.launchOfferDetails}>
              <div className={classes.launchOfferPriceContainer}>
                <Typography
                  variant={TypographyVariant.BodyB6Regular}
                  className={clsx(classes.strikeThrough, classes.oldPrice)}
                >
                  {getCurrencySymbol(firstPlan?.metadata?.currency)}
                  {firstPlan?.metadata?.renewalAmount || 0}
                </Typography>
                <Typography
                  variant={TypographyVariant.BodyB2Bold}
                  className={classes.launchOfferPrice}
                >
                  {getCurrencySymbol(firstPlan?.metadata?.currency)}
                  {firstPlan?.metadata?.offerRenewalAmount || 0}
                </Typography>
                <Typography
                  variant={TypographyVariant.BodyB6Medium}
                  className={classes.perMonth}
                >
                  {t("Paywall_/month")}
                </Typography>
              </div>
              <Typography
                variant={TypographyVariant.BodyB6Regular}
                className={classes.afterPeriod}
              >
                {t("Paywall_after included access period")}
              </Typography>
            </div>
          </div>*/}
          <div className={classes.launchOfferBtnContainer}>
            <div className={classes.startingAt}>
              <Typography
                variant={TypographyVariant.BodyB2Regular}
                color={TypographyColor.Silver}
                className={classes.startingAtLabel}
              >
                {t("Paywall_Starting at")}
              </Typography>
              <Typography
                variant={TypographyVariant.HeadingH2Bold}
                className={classes.bundlePrice}
              >
                {getCurrencySymbol(firstPlan?.metadata?.currency)}
                {firstPlan?.metadata?.netAmount}
              </Typography>
            </div>
            <Button
              size="small"
              onClick={subscribeProController}
              className={classes.buyBtn}
              data-gamepad-no-auto-scroll="true"
            >
              {t("Paywall_BUY BUNDLE")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCloudConsoleCard = () => (
    <div
      className={clsx(classes.planCard, { featured: isFounder })}
      data-paywall-card
    >
      <div className={classes.planText}>
        <Typography
          variant={TypographyVariant.HeadingH4Bold}
          className={classes.planTitle}
        >
          {t("Paywall_Cloud Console Access")}
        </Typography>
        <Typography
          variant={TypographyVariant.BodyB6Regular}
          className={classes.planSubtitle}
        >
          {t("Paywall_For users with a compatible controller")}
        </Typography>
      </div>
      <StaticImage
        src={CloudConsole1x}
        src2x={CloudConsole2x}
        src3x={CloudConsole3x}
        className={classes.cloudConsoleImg}
      />
      <div className={classes.planBottom}>
        <div className={classes.planPriceGroup}>
          <Typography
            variant={TypographyVariant.BodyB2Bold}
            className={classes.planPrice}
          >
            {getCurrencySymbol(withoutController?.metadata?.currency)}
            {withoutController?.metadata?.netAmount}
          </Typography>
          <Typography
            variant={TypographyVariant.BodyB6Regular}
            color={TypographyColor.Silver}
            className={classes.perMonth}
          >
            {t(
              (withoutController?.duration ?? 1) > 1
                ? "Paywall_Per Month"
                : "Paywall_Per Day",
            )}
          </Typography>
        </div>
        <Button
          size="small"
          onClick={subscribeWithoutController}
          className={classes.buyBtn}
          data-gamepad-no-auto-scroll="true"
        >
          {t("Paywall_GET ACCESS")}
        </Button>
      </div>
    </div>
  );

  if (isLoading) return <LoadingScreen />;

  return (
    <div className={classes.root}>
      <div className={clsx(classes.background, { featured: isFounder })} />
      <div className={classes.header} onClick={handleClose}>
        <Icon
          name={"icon-linear-arrow-left-3"}
          className={classes.iconHeader}
          size={isDesktop ? 52 : 24}
          data-button-sound="back"
        />
      </div>
      <div className={classes.headerText}>
        <Typography
          variant={TypographyVariant.HeadingH1Bold}
          className={classes.mainText}
        >
          {t("Paywall_Unlock the gamer in you")}
        </Typography>
        <Typography
          variant={TypographyVariant.BodyB1Regular}
          className={classes.subText}
        >
          {t("Paywall_Everything you need to start playing")}
        </Typography>
      </div>
      <ActivateQrPopup
        onClose={() => setActivateQRLink("")}
        activateLink={activateQRLink}
      />
      <ConfirmSubscriptionPopup
        open={isConfirmOpen}
        price={selectedPlan?.metadata?.netAmount}
        onConfirm={confirmSubscription}
        onClose={closeSubscriptionFlow}
        gst={selectedPlan?.metadata?.taxRate}
        totalAmount={selectedPlan?.planAmount}
        currency={getCurrencySymbol(selectedPlan?.metadata?.currency)}
        taxType={selectedPlan?.metadata?.taxType}
      />
      <BillingInfoPopup
        open={isBillingOpen}
        onContinue={submitBilling}
        onClose={closeSubscriptionFlow}
      />
      <div className={clsx(classes.contentSection, { founder: isFounder })}>
        {isFounder ? (
          renderCloudConsoleCard()
        ) : (
          <>
            {renderBundleCard()}
            {renderCloudConsoleCard()}
          </>
        )}
      </div>
      <div className={classes.bottomBar} ref={bottomBarRef}>
        <Typography
          variant={
            isDesktop
              ? TypographyVariant.BodyB3Medium
              : TypographyVariant.BodyB6Medium
          }
          color={TypographyColor.Silver}
        >
          {t(
            "Paywall_Already have a Pro Controller Bundle? Scan the QR code on your Smart Console access key.",
          )}
        </Typography>
      </div>
      <AccountMismatchPopup
        isOpen={isMismatchPopupOpen}
        onClose={() => setIsMismatchPopupOpen(false)}
      />
    </div>
  );
};

export default Paywall;
